import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {AcDialogService, SessionService, UserMenuSection} from 'ac-infra';
import {SaveConfigurationService} from '../../services/api/general/save-configuration.service';
import {ArmSession} from '../../login/login.component';
import {ArmSessionService} from '../../services/common/arm-session/arm-session.service';
import {Subscription} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {InformationDialogComponent} from '../../dialogs/information-dialog/information-dialog.component';
import {AboutDialogComponent} from '../../dialogs/about-dialog/about-dialog.component';
import {AboutApiService} from '../../services/api/general/about-api.service';
import {LicenseFeaturesService} from '../../services/common/license-features/license-features.service';
import {RoleTypeEnum} from '../../services/enums/role-type.enum';
import * as _ from 'lodash';
import {ChangePasswordDialogComponent} from '../../settings/administration/operators-settings/change-password-dialog/change-password-dialog.component';
import {OperatorsSettingsApiService} from '../../services/api/settings/administration/operators/operators-settings-api.service';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import {LoginApiService} from '../../services/api/login/login-api.service';
import {UserDataService} from '../../services/common/user-data/user-data.service';
import {LiveCloudApi} from '../../services/api/general/live-cloud-api';

@Component({
  selector: 'user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss']
})
export class UserMenuComponent implements OnInit, OnDestroy {
  descriptions: string[];
  username: string;
  sessionTimeout: number = null;
  sessionResetCountDownsubscription: Subscription;
  productVersion: string;
  saasEnabled: boolean;

  @Output() actionClick = new EventEmitter();
  operatorAuthenticationMode = '';
  userSecurityRoles: any;
  fullUserSecurityRoles: any;

  sections: UserMenuSection[];

  constructor(private sessionService: SessionService, private saveConfigurationApiService: SaveConfigurationService,
              private userDataService: UserDataService, private armSessionService: ArmSessionService,
              private dialog: MatDialog, private dialogService: AcDialogService,
              private licenseFeaturesService: LicenseFeaturesService, private acDialogService: AcDialogService,
              private operatorsSettingsApiService: OperatorsSettingsApiService, private aboutApiService: AboutApiService,
              private userPrivilege: UserPrivilegeService, private loginApiService: LoginApiService, private liveCloudApi: LiveCloudApi) {}

  ngOnInit(): void {
    const session: ArmSession = this.userDataService.getData();

    this.descriptions = [session.roleName];
    this.username = session.userName;
    this.userSecurityRoles = this.generateRoleName(session.roles);

    if (session && session.sessionTimeout) {
      this.sessionTimeout = session.sessionStartTime + session.sessionTimeout * 60 * 60 * 1000;
    }

    this.operatorAuthenticationMode = this.generateTypeForDisplay(session.type);

    this.sessionResetCountDownsubscription = this.armSessionService.sessionResetCountdown$.subscribe((event) => {
      this.sessionTimeout = session.sessionStartTime + session.sessionTimeout * 60 * 60 * 1000;
    });

    this.sections = this.generateMenuOptions(session.type);

    this.setAboutInformation();
  }

  setAboutInformation(): void {
    this.aboutApiService.getAboutInformation().then(aboutInformation => {
      this.productVersion = aboutInformation.version;
    });
    this.liveCloudApi.getSaasEnabled().then(saasEnabled => {
      this.saasEnabled = saasEnabled === true;
    });
  }

  about = () => {
    const data: any = {};
    this.aboutApiService.getAboutInformation().then(aboutInformation => {
      data.productVersion = aboutInformation.version;
      this.dialogService.open(AboutDialogComponent, {
        dialogData: data,
        width: 550,
        title: 'About',
        cancelButtonText: 'OK'
      });
    });
  }

  logout = () => {
    this.loginApiService.userLogout().subscribe(() => {
      this.sessionService.endSession();
      this.licenseFeaturesService.reset();
    });
  }

  lock = () => {
    this.sessionService.lockSession();
  }

  changePassword = () => {
    const dialogData = {
      data: {},
      userName: this.username
    };

    this.acDialogService.open(ChangePasswordDialogComponent, {
      dialogData,
      title: 'Update Operator Password',
      onSubmit: () => {
        this.operatorsSettingsApiService.changePassword(dialogData.data).subscribe(() => {
          this.logout();
        });
      }
    });
  }

  saveConfiguration = () => {
    const confirmationMessage = 'Save configuration has started. Download will start in few seconds depending on the ARM database size.';
    const dialogRef = this.dialog.open(InformationDialogComponent, {
      width: '460px',
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(result => {
      dialogRef.close();
    });
    this.saveConfigurationApiService.saveConfiguration();
  }

  ngOnDestroy(): void {
    this.sessionResetCountDownsubscription.unsubscribe();
  }

  onAction($event: any): void {
    this.actionClick.emit();
  }

  private generateRoleName(roles): string {
    if (_.isNil(roles) || _.isEmpty(roles)){
      return '';
    }

    let roleName = roles[0].toLowerCase().replace('_', ' ');
    if (roles[0] === RoleTypeEnum.MONITOR) {
      roleName = roleName + this.generateRolesForDisplay(roles);
    }

    return roleName;
  }

  private generateRolesForDisplay(roles): string {
    const rolesForDisplay = roles.filter(role =>
      (role !== RoleTypeEnum.MONITOR && role !== RoleTypeEnum.ADMIN && role !== RoleTypeEnum.SECURITY_ADMIN));

    let monitorRoles = rolesForDisplay.join(', ').replaceAll('_', ' ');
    this.fullUserSecurityRoles = monitorRoles;

    if (monitorRoles.length > 20) {
      monitorRoles = monitorRoles.substring(0, 20) + '...';
    }

    return !_.isEmpty(rolesForDisplay) ? ` (${monitorRoles})` : '';
  }

  private generateTypeForDisplay(type: any): string {
    switch (type) {
      case 'AZURE': return 'Azure AD';
      case 'RADIUS': return 'RADIUS';
      case 'LDAP': return 'LDAP';
    }

    return 'Local';
  }

  private generateMenuOptions(type: string): UserMenuSection[] {
    const accountSection = [
      {
        message: 'lock',
        callback: this.lock
      },
      {
        message: 'logout',
        callback: this.logout
      }
    ];

    const generalSection = [
      {
        message: 'about',
        callback: this.about
      }
    ];

    if (type === 'OPERATOR') {
      accountSection.unshift({
        message: 'Change Password',
        callback: this.changePassword
      });
    }

    if (this.userPrivilege.isAuthorized()) {
      generalSection.unshift({
        message: 'save configuration',
        callback: this.saveConfiguration
      });
    }

    return [
      {
        sectionTitle: 'Account',
        sectionActions: accountSection
      },
      {
        sectionTitle: 'General',
        sectionActions: generalSection
      }
    ];
  }
}
